import axios from "axios";
import { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import LeftIcon from "../../assets/svgs/leftArrowIcon.svg";
import RightIcon from "../../assets/svgs/rightArrowIcon.svg";
import Loader from "../../components/Loader";
import SearchDropdownList from "../../components/SearchDropdownList";
import useAuth from "../../hooks/useAuth";
import BaseLayout from "../../layouts/BaseLayout";
import { BASE_URL } from "../../utils/Element";
import BuyersDetailsHeader from "./components/BuyersDetailsHeader";

const BuyersDetails = () => {
  const { id } = useParams();
  const [config] = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [buyerData, setBuyerData] = useState();

  const fetchDetails = () => {
    setLoading(true);
    const savedQueryParams = localStorage.getItem("queryParams");
    let localQueryParams = {};

    if (savedQueryParams) {
      localQueryParams = Object.fromEntries(new URLSearchParams(savedQueryParams));
    }

    axios
      .post(`${BASE_URL}/acquisition-getbyid/${id}`, localQueryParams, config)
      .then((res) => setBuyerData(res?.data?.data))
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDetails();
  }, [id]);

  const queryParams = new URLSearchParams(location.search);
  const searchValue = queryParams.get("search");

  // Compact Miles Renderer
  const renderMiles = (data, title) => (
    <div>
      <p className="dark-M body-N mb-2 text-sm">{title}</p>
      <div className="space-y-2 text-sm">
        {[
          { label: "1 Mile", value: data?.oneMile },
          { label: "3 Miles", value: data?.secondMile },
          { label: "5 Miles", value: data?.thirdMile },
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-3">
            <span className="dark-M w-14 font-medium">{item.label}</span>
            <span className="tags green-H body-S flex-1 py-1 px-3">
              {item.value || "—"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <BaseLayout>
      {/* Top Bar */}
      <div className="flex justify-between items-center mb-6">
        <p
          role="button"
          className="green-H head-5 underline"
          onClick={() => navigate(`/buyers?search=${searchValue || ""}`)}
        >
          ← Back to Buyers
        </p>

        <div className="body-S dark-M flex items-center gap-6">
          {buyerData?.previous_id && (
            <div
              role="button"
              onClick={() => navigate(`/buyer/${buyerData.previous_id}`)}
              className="flex items-center gap-2 hover:text-green-H transition-colors"
            >
              <img src={LeftIcon} alt="" /> Previous
            </div>
          )}
          {buyerData?.next_id && (
            <div
              role="button"
              onClick={() => navigate(`/buyer/${buyerData.next_id}`)}
              className="flex items-center gap-2 hover:text-green-H transition-colors"
            >
              Next <img src={RightIcon} alt="" />
            </div>
          )}
        </div>

        <SearchDropdownList from="buyer" />
      </div>

      {/* Main Compact Content */}
      <div className="contact-details light-bg-L p-6 rounded-3xl">
        {loading ? (
          <Loader />
        ) : (
          <>
            <BuyersDetailsHeader buyerData={buyerData} onCallApi={fetchDetails} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
              {/* Search Criteria Card - Compact */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 ">
                <h3 className="dark-M body-L mb-5">SEARCH CRITERIA</h3>

                <div className="grid grid-cols-1 gap-6 text-sm">
                  <div>
                    <p className="dark-M body-N mb-1.5">Min Lease Term Remaining</p>
                    <p className="tags green-H body-S inline-block">
                      {buyerData?.min_lease_term_reamaining
                        ? `${buyerData.min_lease_term_reamaining} Years`
                        : "0 Year"}
                    </p>
                  </div>

                  <div>
                    <p className="dark-M body-N mb-1.5">Availability Status</p>
                    <div className="flex flex-wrap gap-2">
                      {buyerData?.availability_status?.map((el, i) => (
                        <span key={i} className="tags green-H body-S">
                          {el}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="dark-M body-N mb-1.5">Property Type</p>
                    <div className="flex flex-wrap gap-2">
                      {buyerData?.property_type?.map((el, i) => (
                        <span key={i} className="tags green-H body-S">
                          {el?.type}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="dark-M body-N mb-1.5">Landlord Responsibilities</p>
                    <div className="flex flex-wrap gap-2">
                      {buyerData?.landlord_responsibilities?.map((el, i) => (
                        <span key={i} className="tags green-H body-S">
                          {el}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Financial & Location Card - Compact */}
              <div className="bg-white  rounded-2xl p-6 shadow-sm border border-gray-100" >
                <h3 className="dark-M body-L mb-5"></h3>

                <div className="grid grid-cols-3 gap-x-8 gap-y-6 text-sm">
                  <div>
                    <p className="dark-M body-N mb-1">Min Price</p>
                    <p className="tags green-H body-S">
                      ${buyerData?.min_price?.toLocaleString() || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="dark-M body-N mb-1">Max Price</p>
                    <p className="tags green-H body-S">
                      ${buyerData?.max_price?.toLocaleString() || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="dark-M body-N mb-1">Asking Cap Rate</p>
                    <p className="tags green-H body-S">
                      {buyerData?.min_asking_cap_rate}%
                    </p>
                  </div>

                  <div>
                    <p className="dark-M body-N mb-1">Price per SF</p>
                    <p className="tags green-H body-S">{buyerData?.price_sf || "—"}</p>
                  </div>
                  <div>
                    <p className="dark-M body-N mb-1">Rent per SF</p>
                    <p className="tags green-H body-S">{buyerData?.rent_sf || "—"}</p>
                  </div>
                  <div>
                    <p className="dark-M body-N mb-1">Total GLA</p>
                    <p className="tags green-H body-S">{buyerData?.total_gla || "—"}</p>
                  </div>
                </div>

                {/* Demographics & Traffic - Side by Side */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
                  {renderMiles(buyerData?.min_demographics, "Min. Demographics")}
                  {renderMiles(buyerData?.min_traffic_count, "Min. Traffic Counts")}
                </div>
              </div>
            </div>

            {/* Bottom Section - Compact Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
              {/* Tenants */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 ">
                <p className="dark-M body-N mb-3">Tenants</p>
                <div className="flex flex-wrap gap-2">
                  {buyerData?.tenant_details?.length > 0 ? (
                    buyerData.tenant_details.map((el, i) => (
                      <span key={i} className="tags green-H body-S">
                        {el?.tenant_name}
                      </span>
                    ))
                  ) : (
                    <p className="text-gray-400 text-sm">No tenants specified</p>
                  )}
                </div>
              </div>

              {/* State */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 ">
                <p className="dark-M body-N mb-3">State</p>
                <div className="flex flex-wrap gap-2">
                  {buyerData?.state?.length > 0 ? (
                    buyerData.state.map((el, i) => (
                      <span key={i} className="tags green-H body-S">
                        {el}
                      </span>
                    ))
                  ) : (
                    <p className="text-gray-400 text-sm">No state specified</p>
                  )}
                </div>
              </div>

              {/* Comments */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100  md:col-span-1">
                <p className="dark-M body-N mb-3">Comments</p>
                <p className="dark-H text-sm leading-relaxed">
                  {buyerData?.comment || "No comments available."}
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </BaseLayout>
  );
};

export default BuyersDetails;